import { useMemo, useState, type FormEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { saveCostPrice, useCostPrices } from "@/hooks/use-analytics-v2";
import type { UsagePrice, UsagePriceInput } from "@/types/analyticsV2";

/** What the form edits: every rate as typed, so an empty optional field stays "not set". */
interface PriceDraft {
  provider: string;
  model: string;
  inputPerMtok: string;
  cachedInputPerMtok: string;
  cacheWritePerMtok: string;
  outputPerMtok: string;
  perUnit: string;
  effectiveFrom: string;
  /** Editing an existing row: provider and model are fixed. */
  existing: UsagePrice | null;
}

const todayUtc = (): string => new Date().toISOString().slice(0, 10);
const day = (iso: string) => iso.slice(0, 10);
const text = (value: number | null): string =>
  value === null ? "" : String(value);

const draftFor = (
  price: UsagePrice | null,
  preset: { provider: string; model: string } | null = null,
): PriceDraft => ({
  provider: price?.provider ?? preset?.provider ?? "",
  model: price?.model ?? preset?.model ?? "",
  inputPerMtok: price ? text(price.inputPerMtok) : "",
  cachedInputPerMtok: price ? text(price.cachedInputPerMtok) : "",
  cacheWritePerMtok: price ? text(price.cacheWritePerMtok) : "",
  outputPerMtok: price ? text(price.outputPerMtok) : "",
  perUnit: price ? text(price.perUnit) : "",
  // Never before the row being changed starts: a new rate dated earlier would end where it
  // begins and change nothing visible.
  effectiveFrom:
    price && day(price.effectiveFrom) > todayUtc()
      ? day(price.effectiveFrom)
      : todayUtc(),
  existing: price,
});

/** Empty required rates mean 0, empty optional ones mean "use the input rate" (null). */
const inputFromDraft = (draft: PriceDraft): UsagePriceInput => {
  // "0,5" is a decimal comma, not "not set".
  const parse = (value: string) => Number(value.trim().replace(",", "."));
  const required = (value: string) => (value.trim() === "" ? 0 : parse(value));
  const optional = (value: string) =>
    value.trim() === "" ? null : parse(value);
  return {
    provider: draft.provider.trim().toLowerCase(),
    model: draft.model.trim(),
    inputPerMtok: required(draft.inputPerMtok),
    cachedInputPerMtok: optional(draft.cachedInputPerMtok),
    cacheWritePerMtok: optional(draft.cacheWritePerMtok),
    outputPerMtok: required(draft.outputPerMtok),
    perUnit: required(draft.perUnit),
    effectiveFrom: draft.effectiveFrom,
  };
};

const isManual = (price: UsagePrice) => price.source.startsWith("manual");
const rate = (value: number | null) => (value === null ? "—" : `$${value}`);

/**
 * The AI price list (usage_prices). OpenAI / Anthropic / Gemini rates come from the daily
 * Langfuse sync; the rest (Jev, Groq, Cohere, ...) and any correction are set here. A save never
 * edits history: it starts a new rate on a date, and a past date reprices that provider's usage.
 */
export function CostPricesSection() {
  const query = useCostPrices();
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<PriceDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const prices = query.data?.prices;
  const unpriced = query.data?.unpriced ?? [];
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = prices ?? [];
    return needle
      ? rows.filter((p) =>
          `${p.provider} ${p.model}`.toLowerCase().includes(needle),
        )
      : rows;
  }, [prices, search]);

  const open = (next: PriceDraft) => {
    setSaveError(null);
    setDraft(next);
  };

  const onSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft) return;
    const input = inputFromDraft(draft);
    const rates = [
      input.inputPerMtok,
      input.outputPerMtok,
      input.perUnit,
      input.cachedInputPerMtok ?? 0,
      input.cacheWritePerMtok ?? 0,
    ];
    if (!rates.every(Number.isFinite)) {
      setSaveError("Every price must be a number, e.g. 0.59.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await saveCostPrice(input);
      setDraft(null);
    } catch (err) {
      setSaveError(
        err instanceof Error && err.message
          ? err.message
          : "Could not save the price.",
      );
    } finally {
      setSaving(false);
    }
  };

  const field = (
    key: keyof Omit<PriceDraft, "existing">,
    label: string,
    hint?: string,
  ) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`price-${key}`}>{label}</Label>
      <Input
        id={`price-${key}`}
        inputMode={key === "provider" || key === "model" ? "text" : "decimal"}
        autoComplete="off"
        disabled={
          saving ||
          ((key === "provider" || key === "model") && Boolean(draft?.existing))
        }
        value={draft?.[key] ?? ""}
        onChange={(event) =>
          setDraft((d) => (d ? { ...d, [key]: event.target.value } : d))
        }
      />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );

  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="ai-prices-heading"
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h2 id="ai-prices-heading" className="text-base font-medium">
            Prices
          </h2>
          <p className="text-sm text-muted-foreground max-w-3xl">
            OpenAI, Anthropic and Gemini prices update automatically every day.
            Set the others (Jev, Groq, Cohere, new vendors) here, or correct any
            rate. A price applies from its start date; a past date reprices that
            provider&apos;s usage from then within 15 minutes.
          </p>
        </div>
        <Button onClick={() => open(draftFor(null))}>Add price</Button>
      </div>

      {query.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load prices.
        </p>
      ) : query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {unpriced.length > 0 && (
            <div
              role="status"
              className="rounded-md border border-yellow-border bg-yellow-bg p-3 text-sm"
            >
              <p className="font-medium">
                Needs a price — these calls are counted as $0 until one is set:
              </p>
              <ul className="mt-2 flex flex-col gap-1">
                {unpriced.map((u) => (
                  <li
                    key={`${u.provider}|${u.model}`}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <span className="font-mono">
                      {u.provider} / {u.model || "(no model)"}
                    </span>
                    <span className="text-muted-foreground">
                      {u.events.toLocaleString("en-US")} calls in 30 days, last{" "}
                      {u.lastSeen}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        open(
                          draftFor(null, {
                            provider: u.provider,
                            model: u.model,
                          }),
                        )
                      }
                    >
                      Set price
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Input
            aria-label="Filter prices"
            placeholder="Filter by provider or model"
            className="max-w-xs"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <Table aria-label="AI prices">
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>Model</TableHead>
                <TableHead className="text-right">Input / 1M</TableHead>
                <TableHead className="text-right">Cached / 1M</TableHead>
                <TableHead className="text-right">Output / 1M</TableHead>
                <TableHead className="text-right">Per unit</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((price) => (
                <TableRow key={price.id}>
                  <TableCell>{price.provider}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {price.model || "(no model)"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {rate(price.inputPerMtok)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {rate(price.cachedInputPerMtok)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {rate(price.outputPerMtok)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {price.perUnit ? `$${price.perUnit}` : "—"}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {day(price.effectiveFrom) === "2020-01-01"
                      ? "always"
                      : day(price.effectiveFrom)}
                    {price.effectiveTo ? ` → ${day(price.effectiveTo)}` : ""}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Badge
                        variant={isManual(price) ? "secondary" : "outline"}
                        title={price.source}
                      >
                        {isManual(price) ? "Manual" : "Auto"}
                      </Badge>
                      {price.repricePending ? (
                        <Badge variant="warning">Repricing…</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Change price of ${price.provider} ${price.model}`}
                      onClick={() => open(draftFor(price))}
                    >
                      Change
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Dialog
        open={draft !== null}
        onOpenChange={(isOpen) => (isOpen || saving ? null : setDraft(null))}
      >
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={onSave} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>
                {draft?.existing ? "Change price" : "Add price"}
              </DialogTitle>
              <DialogDescription>
                USD per million tokens, as on the vendor&apos;s price page.
                Leave cached and cache write empty to bill them at the input
                rate. Per unit is for calls billed per request or search (Cohere
                rerank, Jev).
              </DialogDescription>
            </DialogHeader>
            {draft?.existing && !isManual(draft.existing) ? (
              <p className="rounded-md border border-yellow-border bg-yellow-bg p-2 text-sm">
                This rate updates automatically from Langfuse. Saving makes it
                manual: the daily sync will stop changing it.
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {field("provider", "Provider", "e.g. openai, groq, typesafe")}
              {field(
                "model",
                "Model",
                "The id the code sends; a prefix covers dated versions",
              )}
              {field("inputPerMtok", "Input $ / 1M tokens")}
              {field("outputPerMtok", "Output $ / 1M tokens")}
              {field("cachedInputPerMtok", "Cached input $ / 1M", "Optional")}
              {field(
                "cacheWritePerMtok",
                "Cache write $ / 1M",
                "Optional (Anthropic)",
              )}
              {field("perUnit", "$ per unit", "Optional")}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="price-effectiveFrom">Applies from (UTC)</Label>
                <Input
                  id="price-effectiveFrom"
                  type="date"
                  disabled={saving}
                  value={draft?.effectiveFrom ?? ""}
                  onChange={(event) =>
                    setDraft((d) =>
                      d ? { ...d, effectiveFrom: event.target.value } : d,
                    )
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Up to 92 days back; earlier usage keeps its old rate.
                </p>
              </div>
            </div>
            {saveError ? (
              <p role="alert" className="text-sm text-destructive">
                {saveError}
              </p>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={saving}
                onClick={() => setDraft(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  saving || !draft?.provider.trim() || !draft?.effectiveFrom
                }
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
